import { createAction } from 'Utility/Redux';
import { IRewardData } from '../../Utility/RewardHelper';
import { AudioStreamType } from '../../System/Audio/AudioSystem';
import { FBTrackingEvent } from '../../Utility/Tracking/FBTrackingEvent';
import { IMessagePayload } from '../Game/IEntryPointData';
import { IEventOffer, IPlayerState, IPlayerStateTaskData, PlayerFTUX } from './IPlayerState';
import { TActionType } from '../../Utility/Tracking/DataSet/Transaction';
import { EventType } from 'Utility/Helpers/Events/EventType';
import { PopupID } from 'State/UI/PopupID';

enum Types {
    OPEN_CROSS_PROMO_MENU = 'openCrossPromoMenu',
    SET_PLAYER_STATE = 'setPlayerState',
    UPDATE_TIMERS = 'updateTimers',
    RECEIVE_COINS = 'receiveCoins',
    RECEIVE_KEYS = 'receiveKeys',
    RECEIVE_BOOSTER_TNT = 'receiveBoosterTNT',
    RECEIVE_BOOSTER_BULLSEYE = 'receiveBoosterBullsEye',
    RECEIVE_BOOSTER_SHUFFLE = 'receiveBoosterShuffle',
    RECEIVE_BOOSTER_UNDO = 'receiveBoosterUndo',
    RECEIVE_BOOSTER_JOKER = 'receiveBoosterJoker',
    DEBUG_RECEIVE_BOOSTER_PLUS_FIVE = 'debugReceiveBoosterPlusFive',
    RECEIVE_EXTRA_CARDS = 'receiveExtraCards',
    SELECT_BACKCARD = 'selectBackCard',
    UNLOCK_BACKCARD = 'unlockBackCard',
    SEE_NEW_BACKCARDS = 'seeNewBackCards',
    RECEIVE_CURRENCY_SHARE_REWARD = 'receiveCurrencyShareReward',
    RECEIVE_EVENT_REWARD = 'receiveEventReward',
    RECEIVE_WHEEL_SPINS = 'receiveWheelSpins',
    SPIN_WHEEL = 'spinWheel',
    CLEAR_TASKS = 'clearTasks',
    SET_VOLUME_SFX = 'setVolumeSFX',
    SET_VOLUME_MUSIC = 'setVolumeMusic',
    SET_VOLUME = 'setVolume',
    RECEIVE_FRIEND_GIFT_REWARD = 'receiveFriendGiftReward',
    UNLOCK_SPIN_WHEEL = 'unlockSpinWheel',
    UNLOCK_FIELD = 'unlockField',
    RECEIVE_REWARDS = 'receiveRewards',
    SET_FTUX_UNLOCKS = 'setFTUXUnlocks',
    CLEAR_FTUX_UNLOCKS = 'clearFTUXUnlocks',
    CLEAR_FIELD = 'clearFields',
    ADD_TASKS = 'addTasks',
    ADD_FTUE_TRACKING_EVENT = 'addFTUETrackingEvent',
    COMPLETE_WHEEL_TIMER = 'completeWheelTimer',
    CLAIM_SHOP_GIFT = 'claimShopGift',
    COMPLETE_FREE_SHOP_TIMER = 'completeFreeShopTimer',
    COMPLETE_AD_COOLDOWN_TIMER = 'completeAdCooldownTimer',
    REFRESH_QUEST_AFTER_COOLDOWN = 'refreshQuestAfterCooldown',
    COMPLETE_LEVEL = 'completeLevel',
    SHOW_LEVEL_AUTO_FLOW_POPUP = 'showLevelAutoFlowPopup',
    SHOW_STARTER_PACK_OFFER_POPUP = 'showStarterPackOfferPopup',
    CLAIM_QUEST_REWARD = 'claimQuestReward',
    CLAIM_QUEST_REWARD_BY_ID = 'claimQuestRewardById',
    HARVEST = 'harvest',
    CLAIM_DAILY_LOGIN_REWARD = 'claimDailyLoginReward',
    UPDATE_LOGIN_STREAK = 'updateLoginStreak',
    SHOW_SUNDAY_OFFER_POPUP = 'showSundayOfferPopup',
    SHOW_PAYER_REACTIVATION_POPUP = 'showPayerReactivationPopup',
    SHOW_OFFER_POPUP = 'showOfferPopup',
    SET_PET_LEVEL = 'setPetLevel',
    AGREE_WITH_TERMS = 'agreeWithTerms',
    SET_BOT_SUBSCRIBED = 'setBotSubscribed',
    SET_CREATED_SHORTCUT = 'setCreatedShortcut',
    ATTEMPT_EVENT_OFFER_PURCHASE = 'attemptEventOfferPurchase',
    SET_DEVICE_ID = 'setDeviceId',
    SET_FACEBOOK_USER_ID = 'setFacebookUserId',
    SET_CURRENT_EVENT_OFFER = 'setCurrentEventOffer',
}

export interface IAudioData {
    type: AudioStreamType;
    enabled: boolean;
}

export interface IFriendGiftRewardActionPayload {
    messageData: IMessagePayload;
    reward: IRewardData;
}

export interface ICoinsActionPayload {
    coins: number;
    showPopup?: boolean;
}

export interface IFTUXUnlockPayload {
    unlocks: PlayerFTUX[];
    rewards?: IRewardData[];
}

export interface IRewardActionPayload {
    rewards: IRewardData[];
    dontTrack?: boolean;
    queueTask?: boolean;
    groupTasks?: boolean;
    rewardFrom?: TActionType;
    id?: string;
    [key: string]: any;
}

export interface IAttemptEventOfferPurchasePayload {
    eventType: EventType;
    popupId: PopupID;
}

export interface IShowLevelAutoFlowPopupParams {
    levelNumber?: number;
    allowRandomizedLevelSelection?: boolean;
    forceSkipLevelPopup?: boolean;
    delay?: number;
}

export const PlayerActions = {
    setCreatedShortcut: createAction(Types.SET_CREATED_SHORTCUT),
    setBotSubscribed: createAction(Types.SET_BOT_SUBSCRIBED, resolve => (isSubscribed: boolean) =>
        resolve(isSubscribed)
    ),
    agreeWithTerms: createAction(Types.AGREE_WITH_TERMS),
    openCrossPromoMenu: createAction(Types.OPEN_CROSS_PROMO_MENU, resolve => (campaign: unknown) => resolve(campaign)),
    /**
     * Should avoid generalized state updates in favor of more specific actions with minimal changes to state.
     * `setPlayerState` will bypass most reducer validation.
     * @deprecated
     */
    setPlayerState: createAction(Types.SET_PLAYER_STATE, resolve => (playerState: IPlayerState) =>
        resolve(playerState)
    ),
    updateTimers: createAction(Types.UPDATE_TIMERS),
    receiveBoosterTNT: createAction(Types.RECEIVE_BOOSTER_TNT, resolve => (amount: number) => resolve<number>(amount)),
    receiveBoosterBullsEye: createAction(Types.RECEIVE_BOOSTER_BULLSEYE, resolve => (amount: number) =>
        resolve<number>(amount)
    ),
    receiveBoosterShuffle: createAction(Types.RECEIVE_BOOSTER_SHUFFLE, resolve => (amount: number) =>
        resolve<number>(amount)
    ),
    receiveBoosterUndo: createAction(Types.RECEIVE_BOOSTER_UNDO, resolve => (amount: number) =>
        resolve<number>(amount)
    ),
    receiveBoosterJoker: createAction(Types.RECEIVE_BOOSTER_JOKER, resolve => (amount: number) =>
        resolve<number>(amount)
    ),
    debugReceiveBoosterPlusFive: createAction(Types.DEBUG_RECEIVE_BOOSTER_PLUS_FIVE, resolve => (amount: number) =>
        resolve<number>(amount)
    ),
    /**@deprecated */
    unlockBackCard: createAction(Types.UNLOCK_BACKCARD, resolve => (card?: number) => resolve<number>(card)),
    /**@deprecated */
    seeNewBackcards: createAction(Types.SEE_NEW_BACKCARDS),
    receiveWheelSpins: createAction(Types.RECEIVE_WHEEL_SPINS, resolve => (spins: number) => resolve<number>(spins)),
    unlockSpinWheel: createAction(Types.UNLOCK_SPIN_WHEEL),
    clearTasks: createAction(Types.CLEAR_TASKS, resolve => (tasksToClear?: IPlayerStateTaskData[]) =>
        resolve<IPlayerStateTaskData[]>(tasksToClear)
    ),
    setVolume: createAction(Types.SET_VOLUME, resolve => (audioData: IAudioData) => resolve<IAudioData>(audioData)),
    receiveCurrencyShareReward: createAction(Types.RECEIVE_CURRENCY_SHARE_REWARD, resolve => (reward: IRewardData) =>
        resolve<IRewardData>(reward)
    ),
    receiveFriendGiftReward: createAction(
        Types.RECEIVE_FRIEND_GIFT_REWARD,
        resolve => (reward: IFriendGiftRewardActionPayload) => resolve<IFriendGiftRewardActionPayload>(reward)
    ),
    unlockFields: createAction(Types.UNLOCK_FIELD, resolve => (...fieldIds: string[]) => resolve<string[]>(fieldIds)),
    setFTUXUnlocks: createAction(Types.SET_FTUX_UNLOCKS, resolve => (unlocks: PlayerFTUX[], rewards?: IRewardData[]) =>
        resolve<IFTUXUnlockPayload>({ unlocks, rewards })
    ),
    clearFTUX: createAction(Types.CLEAR_FTUX_UNLOCKS, resolve => (...typesToClear: PlayerFTUX[]) =>
        resolve<PlayerFTUX[]>(typesToClear)
    ),
    clearField: createAction(Types.CLEAR_FIELD, resolve => (fieldId: string) => resolve<string>(fieldId)),
    receiveRewards: createAction(Types.RECEIVE_REWARDS, resolve => (reward: IRewardActionPayload) =>
        resolve<IRewardActionPayload>(reward)
    ),
    addTasks: createAction(Types.ADD_TASKS, resolve => (taskDatas: Array<IPlayerStateTaskData>) =>
        resolve<Array<IPlayerStateTaskData>>(taskDatas)
    ),
    addFTUETrackingEvent: createAction(Types.ADD_FTUE_TRACKING_EVENT, resolve => (event: FBTrackingEvent) =>
        resolve<FBTrackingEvent>(event)
    ),
    completeWheelTimer: createAction(Types.COMPLETE_WHEEL_TIMER, resolve => () => resolve()),
    completeShopGiftTimer: createAction(Types.COMPLETE_FREE_SHOP_TIMER, resolve => () => resolve()),
    completeAdCooldownTimer: createAction(Types.COMPLETE_AD_COOLDOWN_TIMER, resolve => () => resolve()),
    refreshQuestAfterCooldown: createAction(Types.REFRESH_QUEST_AFTER_COOLDOWN, resolve => (questIndex: number) =>
        resolve<number>(questIndex)
    ),
    /**
     * @deprecated Use claimQuestRewardById instead
     */
    claimQuestReward: createAction(Types.CLAIM_QUEST_REWARD, resolve => (questIndex: number) =>
        resolve<number>(questIndex)
    ),
    claimQuestRewardById: createAction(Types.CLAIM_QUEST_REWARD_BY_ID, resolve => (id: string) => resolve<string>(id)),
    claimShopGift: createAction(Types.CLAIM_SHOP_GIFT, resolve => () => resolve()),
    completeLevel: createAction(Types.COMPLETE_LEVEL, resolve => () => resolve()),
    showLevelAutoFlowPopup: createAction(
        Types.SHOW_LEVEL_AUTO_FLOW_POPUP,
        resolve => (params?: IShowLevelAutoFlowPopupParams) =>
            resolve<IShowLevelAutoFlowPopupParams | undefined>(params)
    ),
    showStarterPackOfferPopup: createAction(Types.SHOW_STARTER_PACK_OFFER_POPUP, resolve => () => resolve()),
    showSundayOfferPopup: createAction(Types.SHOW_SUNDAY_OFFER_POPUP, resolve => () => resolve()),
    showPayerReactivationPopup: createAction(Types.SHOW_PAYER_REACTIVATION_POPUP, resolve => () => resolve()),
    showOfferPopups: createAction(Types.SHOW_OFFER_POPUP, resolve => (ignoreIfAlreadySeen: boolean = false) =>
        resolve(ignoreIfAlreadySeen)
    ),
    harvest: createAction(Types.HARVEST, resolve => (playAutoFlow?: boolean) => resolve<boolean>(playAutoFlow)),
    claimDailyLoginReward: createAction(Types.CLAIM_DAILY_LOGIN_REWARD, resolve => (rewards: IRewardData[]) =>
        resolve<IRewardData[]>(rewards)
    ),
    updateLoginStreak: createAction(Types.UPDATE_LOGIN_STREAK, resolve => (loginStreak: number) =>
        resolve<number>(loginStreak)
    ),
    setPetLevel: createAction(Types.SET_PET_LEVEL, resolve => (levelNumber: number) => resolve<number>(levelNumber)),
    spinWheel: createAction(Types.SPIN_WHEEL, resolve => (isPremium: boolean = false) => resolve(isPremium)),
    setDeviceId: createAction(Types.SET_DEVICE_ID, resolve => (deviceId: string) => resolve(deviceId)),
    setFacebookUserId: createAction(Types.SET_FACEBOOK_USER_ID, resolve => (facebookUserId: string) =>
        resolve(facebookUserId)
    ),
    setCurrentEventOffer: createAction(Types.SET_CURRENT_EVENT_OFFER, resolve => (eventOffer: IEventOffer) =>
        resolve(eventOffer)
    ),
};
